import Close from '@mui/icons-material/Close'
import Send from '@mui/icons-material/Send'
import { CircularProgress, IconButton } from '@mui/material'
import React, { useEffect, useState } from 'react'
import { toast } from 'react-toastify'
import SendAssessmentComponent from 'src/clinic/components/ClinicManageBookingsPage/sendPrescriptionData/components/SendAssessmentComponent'
import SendTherapyComponent from 'src/clinic/components/ClinicManageBookingsPage/sendPrescriptionData/components/SendTherapyComponent'
import { HE_KEY_DIAGNOSIS, HE_KEY_PSYCH_ASSESSMENTS } from 'src/consumer/constants'
import { removeKeyFromObject } from 'src/consumer/helpers/utilFunctions'
import {
  getHealthEntityArrayForSubmission,
  getInitialDataForSettingDaysOfNextVisit,
  getInitialDataForSettingExistingHealthEntity,
  getInitialDataForSettingExistingSelectedTherapies,
  getNextVisitDateForSubmission,
  getSelectedTherapiesArrayForSubmission,
  saveNewHealthEntityList,
} from 'src/mentor/components/PrescriptionMakingPage/PrescriptionMakingPageComponents'
import { markBookedSessionAsCompletedFunction } from 'src/mentor/helpers/utilityFunctions'
import { getMPDataWithoutParams, postMPDataWithoutParams } from 'src/mentor/services'
import { EDVCBookedSlotDetailsContainer } from '../ClinicManageBookingsPageStyles'
import { getIpAddressFromLocalStorage } from 'src/consumer/services/localstorageServices'
import { getIpAddress } from 'src/helper'

export default function SendPrescriptionData({
  bookedEventInfo,
  closePopover,
  refreshBookedSlots,
  InnerComponent = React.Fragment,
  successMessage = 'Therapy Request sent successfully.',
  dialogTitle,
  prescriptionGetApiSignature = '/api/secure/doctor/prescription/get-by-eventId',
  prescriptionSaveApiSignature = '/api/secure/doctor/prescription/save',
  showGallery = false,
  mentorUserId,
  galleryImagesSearch,
  fileUploadApiSignature = "/api/secure/doctor/file/upload",
  mentorProfileData,
}) {
  const clinicId = bookedEventInfo?.clinicId
  const clinicBrandId = bookedEventInfo?.clinicBrandId

  const [prescriptionData, setPrescriptionData] = useState()
  const [selectedDiagnosis, setSelectedDiagnosis] = useState()
  const [nextVisitInputValue, setNextVisitInputValue] = useState()
  const [nextVisitInputType, setNextVisitInputType] = useState()
  const [uploadPrescriptionImages, setUploadPrescriptionImages] = useState([])

  const [selectedTherapies, setSelectedTherapies] = useState()
  const [selectedAssessments, setSelectedAssessments] = useState()

  const [notesToTherapist, setNotesToTherapist] = useState()
  const [notesForAssessments, setNotesForAssessments] = useState()

  const [prescriptionSaveLoading, setPrescriptionSaveLoading] = useState(false)
  const [loading, setLoading] = useState(false)

  //TODO: REUSE THE PRESCRIPTION DATA PROVIDER INSTEAD OF CREATING THE VARIABLES HERE.

  async function setup() {
    setLoading(true)
    let metaPrescription = null
    const response = await getMPDataWithoutParams(
      `${prescriptionGetApiSignature}?eventId=${bookedEventInfo?.id}`,
    )
    if (response.status === 200) {
      metaPrescription = response?.data?.data?.metaPrescription
    } else {
      toast.error('Error getting prescription data')
      return
    }

    setPrescriptionData(metaPrescription)
    setSelectedDiagnosis(
      getInitialDataForSettingExistingHealthEntity(metaPrescription?.prescriptionData?.diagnosis),
    )
    if (metaPrescription?.prescriptionData?.nextVisitDate) {
      setNextVisitInputValue(
        getInitialDataForSettingDaysOfNextVisit(
          metaPrescription?.prescriptionData?.nextVisitDate,
          metaPrescription?.prescriptionData?.createdTime,
          metaPrescription?.prescriptionData?.nextVisitTypeToShow || 'days',
        ),
      )
    }
    if (metaPrescription?.prescriptionData?.nextVisitTypeToShow) {
      setNextVisitInputType(metaPrescription?.prescriptionData?.nextVisitTypeToShow)
    }
    if (metaPrescription?.prescriptionData?.uploadedImages) {
      setUploadPrescriptionImages(metaPrescription?.prescriptionData?.uploadedImages)
    }
    if (metaPrescription?.prescriptionData?.notesToTherapist) {
      setNotesToTherapist(metaPrescription?.prescriptionData?.notesToTherapist)
    }
    if (metaPrescription?.prescriptionData?.notesForAssessments) {
      setNotesForAssessments(metaPrescription?.prescriptionData?.notesForAssessments)
    }
    if (metaPrescription?.prescriptionData?.therapiesNeeded) {
      setSelectedTherapies(
        getInitialDataForSettingExistingSelectedTherapies(
          metaPrescription?.prescriptionData?.therapiesNeeded,
        ),
      )
    }
    if (metaPrescription?.prescriptionData?.psychAssessments) {
      setSelectedAssessments(
        getInitialDataForSettingExistingHealthEntity(
          metaPrescription?.prescriptionData?.psychAssessments,
        ),
      )
    }

    setLoading(false)
  }

  async function savePrescription() {
    setPrescriptionSaveLoading(true)
    const newlySavedItems = {}
    const newDiagnosisToSave = selectedDiagnosis?.filter((d) => !!d.label)
    const newDiagnosis = await saveNewHealthEntities(newDiagnosisToSave, HE_KEY_DIAGNOSIS)

    const newPsychAssessments = await saveNewHealthEntities(
      selectedAssessments,
      HE_KEY_PSYCH_ASSESSMENTS,
    )

    newlySavedItems[HE_KEY_DIAGNOSIS] = newDiagnosis
    newlySavedItems[HE_KEY_PSYCH_ASSESSMENTS] = newPsychAssessments

    async function saveNewHealthEntities(data, healthEntity, isApplyingCompleteObject = false) {
      return await saveNewHealthEntityList(
        data,
        healthEntity,
        clinicId,
        clinicBrandId,
        isApplyingCompleteObject,
      )
    }

    // Get mentor IP address
    let mentorIP = getIpAddressFromLocalStorage()
    if (!mentorIP) {
      // getIpAddress returns null on error, so no need for try-catch
      mentorIP = await getIpAddress() || undefined
    }

    // Get mentor name
    const mentorName = mentorProfileData?.name || 
                       mentorProfileData?.firstName || 
                       localStorage.getItem('userName') || 
                       bookedEventInfo?.mentorName

    let prescriptionData_toSave = {
      ...removeKeyFromObject(prescriptionData?.prescriptionData || {}),
      mentorId: bookedEventInfo?.mentorId,
      patientId: bookedEventInfo?.patientId,
      bookedEventId: bookedEventInfo?.id,
      prescriptionId: prescriptionData?.prescriptionData?.prescriptionId
        ? prescriptionData?.prescriptionData?.prescriptionId
        : '',
      diagnosis: getHealthEntityArrayForSubmission(
        newDiagnosisToSave,
        HE_KEY_DIAGNOSIS,
        clinicId,
        clinicBrandId,
        newlySavedItems,
      ),
      psychAssessments: getHealthEntityArrayForSubmission(
        selectedAssessments,
        HE_KEY_PSYCH_ASSESSMENTS,
        clinicId,
        clinicBrandId,
        newlySavedItems,
      ),
      uploadedImages: uploadPrescriptionImages,
      therapiesNeeded: getSelectedTherapiesArrayForSubmission(selectedTherapies),
      nextVisitDate: getNextVisitDateForSubmission(nextVisitInputValue, nextVisitInputType),
      nextVisitTypeToShow: nextVisitInputType,
      suggestedBookingTherapy:
        InnerComponent === SendTherapyComponent
          ? true
          : prescriptionData?.prescriptionData?.suggestedBookingTherapy,
      notesToTherapist:
        InnerComponent === SendTherapyComponent
          ? notesToTherapist
          : prescriptionData?.prescriptionData?.notesToTherapist,
      notesForAssessment:
        InnerComponent === SendAssessmentComponent
          ? notesForAssessments
          : prescriptionData?.prescriptionData?.notesForAssessment,
      templateId: prescriptionData?.id,
      qrCodeImageId: prescriptionData?.prescriptionData?.qrCodeImageId,
      prescriptionLanguages: ['en'],
      mentorName: mentorName,
      attachPrescriptionForTherapy: InnerComponent === SendTherapyComponent ? false : false,
      attachPrescriptionForAssessment: InnerComponent === SendAssessmentComponent ? false : false,
    }

    // Only add mentorIP if it's available
    if (mentorIP) {
      prescriptionData_toSave.mentorIP = mentorIP
    }

    // Wrap prescription data in EmrReportPayload structure
    const body = {
      prescription: prescriptionData_toSave,
      emrSectionDataList: [],
      screeningReport: null,
      therapyReport: null,
      assessmentReport: null,
    }

    const response = await postMPDataWithoutParams(prescriptionSaveApiSignature, body)
    if (response.status === 200) {
      if (!bookedEventInfo?.markedCompleted) {
        await markBookedSessionAsCompletedFunction(bookedEventInfo, undefined, false)
        refreshBookedSlots()
      }
      closePopover()
      toast.success(successMessage)
    } else {
      toast.error('Something went wrong.')
    }
    setPrescriptionSaveLoading(false)
  }

  useEffect(() => {
    setup()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])
  return (
    <EDVCBookedSlotDetailsContainer style={{ width: '100%' }}>
      <div
        style={{
          background: '#D9D2E9',
          padding: '8px 16px',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
        }}
      >
        <IconButton onClick={closePopover}>
          <Close />
        </IconButton>
        <div className="d-flex align-items-center gap-2">
          <div>{dialogTitle}</div>
          {loading && <CircularProgress size={20} />}
        </div>
        <IconButton
          disabled={loading || prescriptionSaveLoading}
          onClick={() => {
            savePrescription()
          }}
        >
          <Send />
        </IconButton>
      </div>
      <div className="p-3">
        <InnerComponent
          clinicId={clinicId}
          clinicBrandId={clinicBrandId}
          selectedDiagnosis={selectedDiagnosis}
          setSelectedDiagnosis={setSelectedDiagnosis}
          nextVisitInputValue={nextVisitInputValue}
          setNextVisitInputValue={setNextVisitInputValue}
          nextVisitInputType={nextVisitInputType}
          setNextVisitInputType={setNextVisitInputType}
          notesToTherapist={notesToTherapist}
          setNotesToTherapist={setNotesToTherapist}
          notesForAssessments={notesForAssessments}
          setNotesForAssessments={setNotesForAssessments}
          bookedEventInfo={bookedEventInfo}
          setUploadPrescriptionImages={setUploadPrescriptionImages}
          uploadPrescriptionImages={uploadPrescriptionImages}
          loading={loading}
          setLoading={setLoading}
          selectedTherapies={selectedTherapies}
          setSelectedTherapies={setSelectedTherapies}
          selectedAssessments={selectedAssessments}
          setSelectedAssessments={setSelectedAssessments}
          showGallery={showGallery}
          mentorUserId={mentorUserId}
          galleryImagesSearch={galleryImagesSearch}
          fileUploadApiSignature={fileUploadApiSignature}
        />
      </div>
    </EDVCBookedSlotDetailsContainer>
  )
}
